<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToGuider',
	componentName: 'ToGuider'
});
</script>
<template>
	<div v-show="show" :key="langKey" class="to-guider" :class="setClass">
		<div class="to-guider-bg" :style="bg0Style"></div>
		<div class="to-guider-bg" :style="bg1Style"></div>
		<div class="to-guider-bg" :style="bg2Style"></div>
		<div class="to-guider-bg" :style="bg3Style"></div>
		<to-tooltip v-show="show" ref="tooltipRef" class="to-guider-tip" :readonly="false" color="bg" shadow :close-on-blur="false" :is-scroll-close="false" width="35">
			<to-pic v-if="props.pic" class="to-guider-pic" :src="props.pic"></to-pic>
			<div class="to-guider-content">
				<div v-if="title" class="to-guider-title">
					<div class="to-guider-title-text">{{ title }}</div>
				</div>
				<div class="to-guider-desc">
					<div class="to-guider-desc-text" v-html="desc"></div>
				</div>
				<div class="to-guider-fn">
					<to-checkbox v-model="isNever" class="to-guider-checkbox" :label="t('to.guider.dontShowAgain')"></to-checkbox>
					<template v-if="step + 1 === data.length">
						<to-button class="to-guider-restart" @click="handleRestart">{{ t('to.guider.restart') }}</to-button>
						<to-button class="to-guider-finish" @click="handleComplete">{{ t('to.guider.finish') }}</to-button>
					</template>
					<template v-else>
						<to-button class="to-guider-skip" @click="handleClose">{{ t('to.guider.skip') }}</to-button>
						<to-button class="to-guider-next" @click="handleNext">{{ t('to.guider.next') }}</to-button>
					</template>
				</div>
			</div>
		</to-tooltip>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import { t, langKey } from '@touchvue/locale'

// 假设的类型定义
interface GuideStep {
	id: string
	desc?: string
	title?: string
	pic?: string
}

interface TooltipInstance {
	open: (el: HTMLElement) => void
	warning: (msg: string) => void
}

// Props 定义
interface Props {
	data?: GuideStep[]
	auto?: boolean
	mode?: string
	pic?: string
}

const props = withDefaults(defineProps<Props>(), {
	data: () => [],
	auto: false,
	mode: '',
	pic: ''
})

// Emits 定义
const emit = defineEmits<{
	(e: 'opened'): void
	(e: 'closed', isNever: boolean, completed: boolean): void
	(e: 'complete', isNever: boolean, completed: boolean): void
}>()

// 组件名称


// 响应式数据
const isNever = ref(false)
const title = ref('')
const desc = ref('')
const show = ref(false)
const bg0Style = ref({})
const bg1Style = ref({})
const bg2Style = ref({})
const bg3Style = ref({})
const el = ref<HTMLElement | null>(null)
const step = ref(0)
let timer: number | null = null

// 模板引用
const tooltipRef = ref<TooltipInstance | null>(null)

const setClass = computed(() => {
	let arr = []
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	return arr
})

// 监听器
watch(step, () => {
	init()
})

// 生命周期钩子
onMounted(() => {
	// 注意：在 Vue 3 中，需要确保组件正确挂载到 DOM
	if (document.body && typeof document.body.appendChild === 'function') {
		document.body.appendChild(document.createElement('div')) // 这里需要根据实际情况调整
	}

	nextTick(() => {
		if (props.auto && !isNever.value) {
			open()
		}
		init(200)
	})

	window.addEventListener('resize', resize)
})

onBeforeUnmount(() => {
	destroy()
})

// 方法定义
const destroy = () => {
	// 这里需要根据实际挂载方式调整
	if (timer) {
		clearTimeout(timer)
		timer = null
	}
	window.removeEventListener('resize', resize)
}

const resize = () => {
	timer = window.setTimeout(() => {
		init()
		if (timer) {
			clearTimeout(timer)
			timer = null
		}
	}, 200)
}

const init = (during?: number) => {
	let delay = 0
	if (during) {
		delay = during
	}

	const currentStep = props.data[step.value]
	if (!currentStep) return

	const id = currentStep.id
	el.value = document.getElementById(id)

	// if (el.value && el.value.scrollIntoView) {
	// 	el.value.scrollIntoView()
	// } else {
	// 	if (step.value < props.data.length - 1) {
	// 		step.value += 1
	// 	}
	// 	tooltipRef.value?.warning(id + ' 元素未找到')
	// }

	desc.value = currentStep.desc
	title.value = currentStep.title || ''

	const innerTimer = setTimeout(() => {
		let left = 0
		let top = 0

		if (el.value && el.value.getBoundingClientRect) {
			left = el.value.getBoundingClientRect().left
			top = el.value.getBoundingClientRect().top
		}

		const width = el.value?.offsetWidth || 0
		const height = el.value?.offsetHeight || 0

		bg0Style.value = {
			width: left + 'px'
		}

		bg1Style.value = {
			left: left + 'px',
			width: width + 'px',
			height: top + 'px'
		}

		bg2Style.value = {
			left: left + 'px',
			width: width + 'px',
			top: top + height + 'px'
		}

		bg3Style.value = {
			left: left + width + 'px'
		}

		if (el.value) {
			tooltipRef.value?.open(el.value)
		}

		clearTimeout(innerTimer)
	}, delay)
}

const open = () => {
	show.value = true
	emit('opened')

	nextTick(() => {
		step.value = 0
		init()
	})
}

// 新增明确的事件处理函数
const handleNext = () => {
	step.value++
}

const handleClose = () => {
	close()
}

const handleComplete = () => {
	complete()
}

const handleRestart = () => {
	step.value = 0
}

const close = () => {
	show.value = false
	emit('closed', isNever.value, false)
}

const complete = () => {
	close()
	emit('complete', isNever.value, true)
}
// 暴露方法
defineExpose({ open, close })
</script>
